import React from 'react';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import Header from './Header';
import FirestoreHealthIndicator from '../common/FirestoreHealthIndicator';
import { useAuth } from '../../hooks/useAuth';

interface LayoutProps {
  children: React.ReactNode;
  pageTitle?: string; // Titre optionnel pour chaque page
}

/**
 * Layout principal de l'application
 * 
 * Ce composant englobe toutes les pages protégées et fournit :
 * - Une sidebar de navigation persistante (desktop: toujours visible, mobile: toggleable)
 * - Un header avec les infos utilisateur
 * - Une zone de contenu principale avec animations
 * 
 * Utilisation :
 * <Layout pageTitle="Dashboard">
 *   <YourPageContent />
 * </Layout>
 */
const Layout: React.FC<LayoutProps> = ({ children, pageTitle }) => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  if (!user) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Toujours visible sur desktop (lg:translate-x-0), toggleable sur mobile */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Zone principale : Header + Contenu */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
        <Header 
          onMenuClick={() => setSidebarOpen(true)} 
          pageTitle={pageTitle}
        />
        
        {/* Indicateur de santé Firestore */}
        <div className="px-4 py-2 bg-white border-b border-gray-200">
          <FirestoreHealthIndicator showDetails={false} className="justify-end" />
        </div>
        
        {/* Zone de contenu avec animations douces */}
        <motion.main 
          key={pageTitle} // Force re-animation au changement de page
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="flex-1 overflow-auto p-4 md:p-6 lg:p-8"
        >
          {/* Conteneur de contenu centré dans l'espace disponible */}
          <div className="w-full max-w-7xl mx-auto">
            {children}
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default Layout;